import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, Video, Check, X, Clock3, ExternalLink, Plug, RefreshCw, Settings2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const apiErr = (e, fb) => e?.response?.data?.detail || fb;

function minutesUntil(iso) {
  if (!iso) return null;
  return (new Date(iso) - new Date()) / 60000;
}
function fmtEventTime(iso, allDay) {
  if (!iso) return "";
  if (allDay) {
    try { return new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" }); } catch { return iso; }
  }
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const t = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `Hoy ${t}` : `${d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })} ${t}`;
}

// Configuración OAuth (solo dirección) cuando aún no está configurado.
function ConfigForm({ redirectUri, onSaved }) {
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!cid.trim() || !secret.trim()) { toast.error("Rellena Client ID y Client Secret"); return; }
    setBusy(true);
    try {
      await api.post("/gcal/config", { client_id: cid.trim(), client_secret: secret.trim(), redirect_uri: redirectUri });
      toast.success("Google Calendar configurado");
      onSaved();
    } catch (e) { toast.error(apiErr(e, "No se pudo guardar")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="gcal-config-form">
      <p className="text-xs text-muted-foreground">Configura el cliente OAuth de Google Cloud (una sola vez). Añade esta <strong>URI de redirección</strong> en Google Cloud Console:</p>
      <code className="block break-all rounded bg-muted px-2 py-1 text-[10px]">{redirectUri}</code>
      <div><Label className="text-[11px]">Client ID</Label><Input value={cid} onChange={(e) => setCid(e.target.value)} className="mt-0.5 h-8 text-xs" data-testid="gcal-cfg-client-id" /></div>
      <div><Label className="text-[11px]">Client Secret</Label><Input value={secret} onChange={(e) => setSecret(e.target.value)} type="password" className="mt-0.5 h-8 text-xs" data-testid="gcal-cfg-client-secret" /></div>
      <Button size="sm" className="w-full" disabled={busy} onClick={save} data-testid="gcal-cfg-save">Guardar configuración</Button>
    </div>
  );
}

export default function CalendarQuickAccess() {
  const qc = useQueryClient();
  const [showCfg, setShowCfg] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const notifiedRef = useRef(new Set());
  const syncedRef = useRef(false);

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ["gcal-status"],
    queryFn: async () => (await api.get("/gcal/status")).data,
    retry: false, staleTime: 5 * 60 * 1000,
  });
  const configured = !!status?.configured;
  const connected = !!status?.connected;
  const canConfigure = !!status?.can_configure;

  const { data: up } = useQuery({
    queryKey: ["gcal-upcoming"],
    queryFn: async () => (await api.get("/gcal/events/upcoming?max_results=20")).data,
    enabled: connected, retry: false, refetchInterval: connected ? 60000 : false,
  });
  const events = up?.events || [];
  const pending = up?.pending_invites || 0;

  // Aviso in-app: "reunión en ~5 min" (petición del usuario).
  useEffect(() => {
    if (!connected) return;
    events.forEach((ev) => {
      if (ev.all_day) return;
      const m = minutesUntil(ev.start);
      if (m !== null && m > 0 && m <= 5 && !notifiedRef.current.has(ev.id)) {
        notifiedRef.current.add(ev.id);
        toast.info(`📅 Reunión en ${Math.max(1, Math.round(m))} min`, { description: ev.summary, duration: 10000 });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [up]);

  const imminent = connected && events.some((ev) => { const m = minutesUntil(ev.start); return !ev.all_day && m !== null && m > 0 && m <= 15; });

  const connect = async () => {
    try {
      const { data } = await api.get("/gcal/auth-url");
      const w = window.open(data.url, "gcal_oauth", "width=520,height=680");
      const timer = setInterval(() => {
        if (!w || w.closed) { clearInterval(timer); refetchStatus(); qc.invalidateQueries({ queryKey: ["gcal-upcoming"] }); }
      }, 1000);
    } catch (e) { toast.error(apiErr(e, "No se pudo iniciar la conexión")); }
  };
  const disconnect = async () => {
    try { await api.post("/gcal/disconnect"); toast.success("Google Calendar desconectado"); refetchStatus(); }
    catch (e) { toast.error(apiErr(e, "Error")); }
  };
  const respond = async (ev, response) => {
    try {
      await api.post(`/gcal/events/${ev.id}/respond`, { response });
      toast.success(response === "accepted" ? "Invitación aceptada" : response === "declined" ? "Invitación rechazada" : "Marcada como quizás");
      qc.invalidateQueries({ queryKey: ["gcal-upcoming"] });
    } catch (e) { toast.error(apiErr(e, "No se pudo responder")); }
  };
  const syncPortal = async (silent) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/gcal/sync-portal");
      if (!silent) {
        const parts = [];
        if (data.created) parts.push(`${data.created} nuevos`);
        if (data.updated) parts.push(`${data.updated} actualizados`);
        if (data.deleted) parts.push(`${data.deleted} eliminados`);
        toast.success("Agenda de SOFI sincronizada con Google", { description: parts.length ? parts.join(" · ") : "Todo al día" });
      }
      qc.invalidateQueries({ queryKey: ["gcal-upcoming"] });
    } catch (e) { if (!silent) toast.error(apiErr(e, "No se pudo sincronizar")); } finally { setSyncing(false); }
  };

  // Al detectar conexión por primera vez, sincroniza SOFI -> Google una vez.
  useEffect(() => {
    if (connected && !syncedRef.current) {
      syncedRef.current = true;
      syncPortal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  const dot = imminent ? "bg-rose-500 animate-pulse" : (pending > 0 ? "bg-amber-500" : (connected ? "bg-emerald-500" : "bg-slate-300"));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="calendar-quick-access"
          title="Mi agenda (Google Calendar)"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-accent"
        >
          <CalendarClock className="h-[18px] w-[18px]" />
          <span className={`absolute right-1 top-1 h-2 w-2 rounded-full ${dot}`} />
          {pending > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white" data-testid="gcal-pending-badge">{pending}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="calendar-quick-popover">
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-sm font-semibold"><CalendarClock className="h-4 w-4 text-primary" />Mi agenda</p>
          {connected && (
            <button onClick={() => qc.invalidateQueries({ queryKey: ["gcal-upcoming"] })} className="text-muted-foreground hover:text-foreground" title="Actualizar" data-testid="gcal-refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
          )}
        </div>

        {/* No configurado */}
        {!configured && (
          <div className="space-y-2 p-3">
            {canConfigure ? (
              showCfg ? (
                <ConfigForm redirectUri={status?.redirect_uri} onSaved={() => { setShowCfg(false); refetchStatus(); }} />
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">La integración con Google Calendar aún no está configurada.</p>
                  <Button size="sm" variant="outline" className="w-full" onClick={() => setShowCfg(true)} data-testid="gcal-open-config"><Settings2 className="mr-1.5 h-4 w-4" />Configurar Google Calendar</Button>
                </>
              )
            ) : (
              <p className="text-xs text-muted-foreground">Pídele a dirección que configure la conexión con Google Calendar para poder sincronizar tu agenda.</p>
            )}
          </div>
        )}

        {/* Configurado pero no conectado */}
        {configured && !connected && (
          <div className="space-y-2 p-3">
            <p className="text-xs text-muted-foreground">Conecta tu cuenta de Google para ver tus reuniones aquí, recibir avisos y aceptar invitaciones sin salir de SOFI.</p>
            <Button size="sm" className="w-full" onClick={connect} data-testid="gcal-connect"><Plug className="mr-1.5 h-4 w-4" />Conectar Google Calendar</Button>
          </div>
        )}

        {/* Conectado */}
        {connected && (
          <div className="max-h-96 overflow-auto p-2">
            {events.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">No tienes reuniones próximas.</p>
            ) : (
              <ul className="space-y-1.5">
                {events.map((ev) => {
                  const m = minutesUntil(ev.start);
                  const soon = !ev.all_day && m !== null && m > 0 && m <= 15;
                  return (
                    <li key={ev.id} className={`rounded-lg border p-2.5 ${soon ? "border-rose-300 bg-rose-50" : ev.pending_invite ? "border-amber-300 bg-amber-50" : "border-border"}`} data-testid={`gcal-event-${ev.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{ev.summary}</p>
                          <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Clock3 className="h-3 w-3" />{fmtEventTime(ev.start, ev.all_day)}
                            {soon && <span className="font-semibold text-rose-600">· en {Math.max(1, Math.round(m))} min</span>}
                          </p>
                          {ev.location && <p className="truncate text-[11px] text-muted-foreground">{ev.location}</p>}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {ev.meet_link && <a href={ev.meet_link} target="_blank" rel="noreferrer" className="text-primary hover:opacity-70" title="Unirse (Meet)"><Video className="h-4 w-4" /></a>}
                          {ev.html_link && <a href={ev.html_link} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title="Abrir en Google"><ExternalLink className="h-3.5 w-3.5" /></a>}
                        </div>
                      </div>
                      {ev.pending_invite && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="mr-auto text-[10px] font-semibold uppercase text-amber-600">Invitación</span>
                          <Button size="sm" className="h-7 px-2 text-xs" onClick={() => respond(ev, "accepted")} data-testid={`gcal-accept-${ev.id}`}><Check className="mr-1 h-3.5 w-3.5" />Aceptar</Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => respond(ev, "tentative")} data-testid={`gcal-tentative-${ev.id}`}>Quizás</Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-rose-600" onClick={() => respond(ev, "declined")} data-testid={`gcal-decline-${ev.id}`}><X className="mr-1 h-3.5 w-3.5" />No</Button>
                        </div>
                      )}
                      {!ev.pending_invite && ev.self_status && ev.self_status !== "accepted" && (
                        <p className="mt-1 text-[10px] text-muted-foreground">Tu respuesta: {ev.self_status === "declined" ? "rechazada" : ev.self_status === "tentative" ? "quizás" : ev.self_status}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-2 flex items-center justify-between border-t px-1 pt-2">
              <span className="truncate text-[11px] text-muted-foreground">{up?.google_email}</span>
              <button onClick={disconnect} className="text-[11px] text-muted-foreground underline hover:text-rose-600" data-testid="gcal-disconnect">Desconectar</button>
            </div>
            <Button size="sm" variant="outline" className="mt-2 w-full" disabled={syncing} onClick={() => syncPortal(false)} data-testid="gcal-sync-portal">
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />Sincronizar mi agenda de SOFI → Google
            </Button>
            <p className="mt-1 px-1 text-[10px] text-muted-foreground">Vacaciones, ausencias, teletrabajo y guardias del Portal del Empleado se copian a tu Google Calendar.</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
